import PageHeader from "@/components/site/PageHeader";
import { ABOUT_IMAGE_1, ABOUT_IMAGE_2 } from "@/lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CTABanner from "@/components/site/CTABanner";
import usePageTitle from "@/lib/usePageTitle";
import Reveal from "@/components/site/Reveal";

const VALUES = [
  { title: "Clarity", text: "Clear systems, communication, and expectations create stronger businesses and better decisions." },
  { title: "Stability", text: "Sustainable growth and operational consistency matter more than chaos-driven scaling." },
  { title: "Strategic Thinking", text: "Decisions should be intentional, measurable, and aligned with long-term goals." },
  { title: "Simplicity", text: "Complexity creates overwhelm. The goal is to simplify, reduce friction, and create cleaner systems." },
  { title: "Accountability", text: "Performance should be evaluated honestly through data, feedback, and measurable outcomes." },
  { title: "Sustainable Growth", text: "Businesses should be built in ways that are scalable and supportive of long-term quality of life." },
];

export default function About() {
  usePageTitle("About");
  return (
    <div data-testid="page-about" className="bg-ivory">
      <PageHeader
        overline="About Sarah"
        title="I see what others miss —"
        accent="so you can grow with confidence."
        description="I'm not here to sell hustle, hype, or a louder version of what's already out there. I'm here to help you see your business clearly — and make decisions that actually move it forward."
        testId="about-header"
      />

      {/* Story */}
      <section className="py-16 lg:py-24" data-testid="about-story">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMAGE_1} alt="Sarah Atkinson" className="w-full h-full object-cover" />
            </div>
            <p className="font-ui uppercase tracking-[0.28em] text-[10px] text-charcoal/45 mt-5">
              — Sarah Atkinson · Founder
            </p>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="overline mb-5">My Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-navy leading-[1.15]">
              Strategy is most useful when it feels <em className="font-serif italic text-sage">calm.</em>
            </h2>
            <div className="font-sans text-charcoal/80 mt-6 space-y-5 leading-relaxed">
              <p>
                I work as an adjacent complement to property managers — not replacing them, but helping the owner
                see the bigger strategic picture. My role is the third point of support between you, your manager,
                and the business itself.
              </p>
              <p>
                I help owners identify revenue leaks, improve listing conversion, strengthen guest experience, and
                build systems so the property becomes more stable, profitable, and easier to run.
              </p>
              <p>
                What I notice first is rarely what most people are looking at. The top three photos. The title.
                The hidden friction guests feel before they articulate it. These small details quietly shape trust,
                conversion, and your bottom line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="about-values">
        <div className="container-editorial">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">Core Values</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-navy leading-[1.15]">
              What I bring to every <em className="font-serif italic text-sage">conversation.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 100}>
                <div data-testid={`about-value-${i}`} className="border-l-2 border-gold pl-6">
                  <p className="font-ui uppercase tracking-[0.16em] text-[13px] font-semibold text-navy mb-3">0{i + 1} · {v.title}</p>
                  <p className="font-sans text-[15px] text-charcoal/80 leading-[1.75]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Personal note */}
      <section className="py-20 lg:py-28" data-testid="about-personal-note">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="overline mb-5">Personal Note</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-navy leading-[1.15]">
              The business should feel <em className="font-serif italic text-sage">good to run.</em>
            </h2>
            <p className="font-sans text-charcoal/80 mt-6 leading-relaxed">
              I believe great Airbnb businesses are designed intentionally — not operated reactively. My work
              focuses on the quiet structure beneath the bookings: positioning, systems, communication, and the
              guest experience guests feel before they ever click "Reserve."
            </p>
            <p className="font-sans text-charcoal/80 mt-4 leading-relaxed">
              The goal isn't more. It's <em>clearer</em>, <em>steadier</em>, and <em>more freeing</em>.
            </p>
            <Link to="/contact" data-testid="about-cta-contact" className="btn-primary mt-8">
              Start a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMAGE_2} alt="Sarah Atkinson" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
